import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';
import {
  TGeoObjectType,
  useGeoObjectType,
} from 'components/domain/fdr-geo/fdr-geo-object.utils';
import { TFdrThGeoModuleObject } from 'components/domain/fdr-geo/fdr-geo.types';
import FdrGeoInformationSection from 'components/domain/fdr-geo/information-section/fdr-geo-information-section';
import FdrGeoNavigation from 'components/domain/fdr-geo/navigation/fdr-geo-navigation';
import useMap from 'components/domain/fdr-geo/useMap';
import { FdrHeroSection } from 'components/domain/fdr-hero-section/fdr-hero-section';
import { FdrPageComponentsFragment } from 'schemas/fragment/page-components/fdr-page-components.generated';
import { FdrGeoContentQuery } from 'schemas/query/geo/fdr-geo-content.generated';

export { getServerSideProps } from 'components/page/geo/geo.gssp';

interface IGeoPageProps {
  id: string;
  currentUri: string;
  isCountry: boolean;
  geo: FdrGeoContentQuery['fdrGeo'];
  isB2B: boolean;
  geoModules: TFdrThGeoModuleObject;
  isNewMapDesignEnabled: boolean;
  pageComponents?: FdrPageComponentsFragment;
  geoStaticPageComponents?: FdrPageComponentsFragment;
}

const FdrGeoContent = dynamic(
  () => import('components/domain/fdr-geo/fdr-geo-content'),
  {
    ssr: true,
  }
);

const renderGeoNavigation = (
  geo: FdrGeoContentQuery['fdrGeo'],
  geoModules: TFdrThGeoModuleObject,
  pageComponents: FdrPageComponentsFragment,
  geoObjectType: TGeoObjectType
) => {
  return (
    <FdrGeoNavigation
      key={`geo-navigation`}
      geo={geo}
      geoModules={geoModules}
      pageComponents={pageComponents}
      geoObjectType={geoObjectType}
    />
  );
};

const renderGeoInfo = (
  geo: FdrGeoContentQuery['fdrGeo'],
  geoModules: TFdrThGeoModuleObject
) => {
  return (
    <FdrGeoInformationSection
      key="geo-info-short"
      geo={geo}
      geoModules={geoModules}
    />
  );
};

const renderHero = (geo: FdrGeoContentQuery['fdrGeo'], tagLine: string) => {
  const { bookmarked, hero } = geo || {};

  if (!hero) return null;

  return (
    <FdrHeroSection
      hero={hero}
      title={geo.name}
      text={tagLine}
      bookmarked={bookmarked}
      key="hero"
    />
  );
};

const GeoPage: NextPage<IGeoPageProps> = ({
  id,
  isCountry,
  geo,
  isB2B,
  geoModules,
  isNewMapDesignEnabled,
  pageComponents,
  geoStaticPageComponents,
}) => {
  const geoInformation = useMemo(
    () => renderGeoInfo(geo, geoModules),
    [geo, geoModules]
  );
  const hero = useMemo(() => renderHero(geo, geoModules.tagLine?.text), [geo]);
  const geoObjectType = useGeoObjectType(id);

  const geoNavigation = useMemo(() => {
    return renderGeoNavigation(
      geo,
      geoModules,
      geoStaticPageComponents,
      geoObjectType
    );
  }, [geo, geoModules, geoObjectType]);

  const mapRender = useMap(geo, isCountry, isB2B, isNewMapDesignEnabled, id);

  return (
    <>
      {hero}
      {geoNavigation}
      {geoInformation}
      {isB2B && mapRender()}
      <FdrGeoContent
        geo={geo}
        isNewMapDesignEnabled={isNewMapDesignEnabled}
        geoModules={geoModules}
        id={id}
        isB2B={isB2B}
        isCountry={isCountry}
        pageComponents={
          pageComponents?.components?.length ? pageComponents.components : []
        }
        geoStaticPageComponents={
          geoStaticPageComponents?.components?.length
            ? geoStaticPageComponents.components
            : []
        }
      />
    </>
  );
};

export default GeoPage;
